/**
 * Dynamic attribute form (BON-161). Renders one input per active attribute
 * definition for a category, typed by the definition's dataType. Shared by the
 * item create/edit modal, the request modal, and (later) the submission form.
 */
import type { AttributeDefinition, AttributeValues } from '../../../api/inventory-catalog';

interface Props {
  definitions: AttributeDefinition[];
  values: AttributeValues;
  errors?: Record<string, string>;
  onChange: (next: AttributeValues) => void;
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-moss-500';

function EnumSelect({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function Control({
  def,
  value,
  onChange,
}: {
  def: AttributeDefinition;
  value: string;
  onChange: (v: string) => void;
}) {
  if (def.dataType === 'enum') {
    const allowed = Array.isArray(def.allowedValues) ? (def.allowedValues as string[]) : [];
    return <EnumSelect options={allowed} value={value} onChange={onChange} />;
  }
  if (def.dataType === 'boolean') {
    return <EnumSelect options={['true', 'false']} value={value} onChange={onChange} />;
  }
  const numeric = def.dataType === 'number' || def.dataType === 'duration_days';
  return (
    <input
      type={numeric ? 'number' : 'text'}
      className={inputClass}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function Field({
  def,
  value,
  error,
  onChange,
}: {
  def: AttributeDefinition;
  value: unknown;
  error?: string;
  onChange: (v: unknown) => void;
}) {
  const str = value === undefined || value === null ? '' : String(value);
  return (
    <label className="block">
      <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
        {def.name}
        {def.isRequired && <span className="text-red-500"> *</span>}
      </span>
      <Control def={def} value={str} onChange={onChange} />
      {def.description && (
        <span className="mt-0.5 block text-xs text-stone-400">{def.description}</span>
      )}
      {error && (
        <span className="mt-0.5 block text-xs text-red-600 dark:text-red-400">{error}</span>
      )}
    </label>
  );
}

export default function AttributeForm({ definitions, values, errors, onChange }: Props) {
  const active = definitions.filter((d) => d.isActive);
  if (active.length === 0) {
    return <p className="text-sm text-stone-400">No attributes defined for this category.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {active.map((def) => (
        <Field
          key={def.id}
          def={def}
          value={values[def.name]}
          error={errors?.[def.name]}
          onChange={(v) => onChange({ ...values, [def.name]: v })}
        />
      ))}
    </div>
  );
}
